/**
 * Pulse brand avatar — hand-crafted SVG "S" (NO font dependency).
 *
 * Rendered as a filled circular medallion with:
 *  - Dark `#0B1220` core (slightly deeper than SmartPublish bg)
 *  - Purple #6366F1 outer glow + Cyan #0EA5E9 secondary accent (behind)
 *  - Pure white bold "S" dead-centered via `translate` in the SVG viewBox
 *
 * The "S" is a filled geometric path tuned to read as a crisp mark at both
 * 20px (mobile orb) and 24px (desktop orb). It is NOT a text character.
 */

/* ---- the S path ------------------------------------------------------- *
 *  viewBox: 24×24. The glyph takes up roughly the inner 14×16 area.
 *  Design: two near-mirror horizontal "lobes" joined by a diagonal
 *  stem — bold, geometric, high contrast at small sizes.
 * ---------------------------------------------------------------------- */
export const SMARTPUBLISH_S_PATH =
    "M17.9 7.7c0-1.9-2.3-3.2-5.3-3.2-3.3 0-5.6 1.5-5.6 3.6 0 1.6 1.3 2.5 4.3 3.1l2.5.5c3.8.8 5.9 2.1 5.9 4.6 0 2.6-2.8 4.7-6.9 4.7-4 0-6.9-1.8-7.3-4.4h2.9c.3 1.3 2.1 2.3 4.4 2.3 2.6 0 4.1-.9 4.1-2.3 0-1.2-1.1-1.9-3.8-2.4l-2.5-.5c-3.5-.7-5.3-2-5.3-4.3 0-2.9 3-5.1 7.3-5.1 4 0 6.8 1.9 7 4.4h-2.7z";

export default function PulseAvatar({
    size = 56,
    showPulse = false,
    glowIntensity = 1,
    className = "",
    "data-testid": testId,
}) {
    // Scale the "S" inside the medallion. Center the viewBox at 12,12.
    // Glyph viewBox is 24; medallion is `size` (in px). Everything else
    // scales from these two values so the mark lands perfectly centered.
    const halo1 = `0 0 ${18 * glowIntensity}px rgba(99,102,241,0.55)`;
    const halo2 = `0 0 ${30 * glowIntensity}px rgba(14,165,233,0.3)`;
    return (
        <span
            className={`relative inline-flex ${className}`}
            style={{ width: size, height: size }}
            data-testid={testId}
            aria-hidden
        >
            {/* Secondary cyan halo (behind) */}
            <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                    width: size * 1.6,
                    height: size * 1.6,
                    background:
                        "radial-gradient(circle, rgba(14,165,233,0.35) 0%, transparent 70%)",
                    filter: "blur(10px)",
                    opacity: 0.65,
                }}
            />
            {showPulse && (
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 animate-pulse rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(99,102,241,0.4) 0%, transparent 70%)",
                        filter: "blur(8px)",
                    }}
                />
            )}

            {/* Core medallion */}
            <span
                className="relative z-10 inline-flex h-full w-full items-center justify-center rounded-full"
                style={{
                    backgroundColor: "#0B1220",
                    border: "1px solid rgba(99,102,241,0.6)",
                    boxShadow: `${halo1}, ${halo2}, inset 0 0 0 1px rgba(255,255,255,0.04)`,
                }}
            >
                <svg
                    width={Math.round(size * 0.44)}
                    height={Math.round(size * 0.44)}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                    style={{
                        /* Negative letter-spacing-equivalent: shift path left
                         * 0.5px via transform so the heavier left side sits
                         * optically centered. */
                        transform: "translateX(-0.3px)",
                    }}
                >
                    <path d={SMARTPUBLISH_S_PATH} fill="#FFFFFF" />
                </svg>
            </span>
        </span>
    );
}
